import React, { useState, useEffect } from "react";
import { asText } from "../lib/utils";
import axios from "axios";
import { pollJobUntilDone } from "@/lib/pollJob";
import { ZohoPushStatusBadge } from "@/components/ZohoPushStatusBadge";
import useBranchStore from "@/store/branchStore";
import useAuthStore from "@/store/authStore";
import PermButton from "@/components/PermButton";
import { Plus, Download, Package, Trash2, Calculator, FileText, Loader2, Settings2, Eye, Save, Pencil, Send, AlertTriangle, Paperclip, Search, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ManageTdsDialog from "@/components/ManageTdsDialog";
import RMInwardBillDetailDialog from "@/components/RMInwardBillDetailDialog";
import { formatApiError } from "@/lib/apiError";
import { resolveTypedRM, filterRMsForSearch } from "@/lib/rmResolver";
import Pagination from "@/components/Pagination";
import { Badge } from "@/components/ui/badge";
import { DATE_MODES, defaultTimeline, resolveDateRange, describeTimeline } from "@/lib/timelineFilter";
import { buildBillParams, fetchAllRmInwardBills } from "@/lib/rmInwardExport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_TERMS = [
  { value: "NET_7", label: "Net 7", days: 7 },
  { value: "NET_15", label: "Net 15", days: 15 },
  { value: "NET_30", label: "Net 30", days: 30 },
  { value: "NET_45", label: "Net 45", days: 45 },
  { value: "NET_60", label: "Net 60", days: 60 },
  { value: "DUE_ON_RECEIPT", label: "Due on Receipt", days: 0 },
  { value: "CUSTOM", label: "Custom", days: null }
];

const TAX_OPTIONS = [
  { value: "NONE", label: "None", rate: 0 },
  { value: "GST_5", label: "GST 5%", rate: 5 },
  { value: "GST_12", label: "GST 12%", rate: 12 },
  { value: "GST_18", label: "GST 18%", rate: 18 },
  { value: "GST_28", label: "GST 28%", rate: 28 }
];

// Map local tax values to approximate rates for amount calculation
const LOCAL_TAX_RATES = {
  "NONE": 0, "GST_5": 5, "GST_12": 12, "GST_18": 18, "GST_28": 28
};

const TDS_NONE = { id: "NONE", label: "None", rate: 0 };

const RMInward = ({ prefill = null, onPrefillConsumed = null } = {}) => {
  const { selectedBranch } = useBranchStore();
  const { token } = useAuthStore();
  const [entries, setEntries] = useState([]);
  const [availableRMs, setAvailableRMs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [jobWorkerVendors, setJobWorkerVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchInventory, setBranchInventory] = useState({});
  const [filteredRMs, setFilteredRMs] = useState([]);
  const [rmSearch, setRmSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  // When set, the dialog is in EDIT mode for that bill_id and submitButton
  // becomes PUT instead of POST. Cleared on close/resetForm.
  const [editingBillId, setEditingBillId] = useState(null);
  // Jul 2026 — Playfield Draft state:
  //   editingDraft=true → dialog Save routes to PUT /bills/{id}/draft
  //   (no Zoho, no stock).
  // Sep 2026 — statusFilter is now a SERVER-side filter (sent as the
  // `status` query param); no client-side row filtering remains.
  const [editingDraft, setEditingDraft] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [vendorSearch, setVendorSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Per-page branch filter. Sep 2026 — defaults to "ALL" branches
  // (matching the no-invoice tab); the global BranchSelector only
  // re-syncs it when the user actively switches branch mid-session.
  // Value of "ALL" → don't pass branch param to the API (backend returns all).
  const [viewBranch, setViewBranch] = useState("ALL");
  // Sep 2026 — server-side pagination + filter state (same pattern as
  // the no-invoice tab). Every filter is applied by the backend over
  // the full collection; `total` and `stats` come from the response.
  const [searchText, setSearchText] = useState("");
  const [timeline, setTimeline] = useState(defaultTimeline);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ bills_this_month: 0, qty_this_month: 0, value_this_month: 0 });
  
  // Zoho integration state
  const [zohoAccounts, setZohoAccounts] = useState([]);
  const [zohoConfigured, setZohoConfigured] = useState(false);
  const [defaultAccountId, setDefaultAccountId] = useState("");
  const [zohoAccountsError, setZohoAccountsError] = useState("");
  const [zohoTaxes, setZohoTaxes] = useState([]);

  // RM categories (used to auto-fill the Zoho GL account based on the RM's
  // `category` → `zoho_account_type` mapping).
  const [rmCategories, setRmCategories] = useState([]);

  // Local TDS Tax mappings (from /api/tds-taxes)
  const [tdsTaxes, setTdsTaxes] = useState([]);
  const [showManageTds, setShowManageTds] = useState(false);

  // Bill detail viewer
  const [viewBillId, setViewBillId] = useState(null);

  // Bill/Invoice Form State
  const [billData, setBillData] = useState({
    vendor_id: "",
    vendor_name: "",
    branch_id: "",
    bill_number: "",
    order_number: "",
    bill_date: new Date().toISOString().split('T')[0],
    due_date: "",
    payment_terms: "DUE_ON_RECEIPT",
    accounts_payable: "Trade Payables",
    reverse_charge: false,
    notes: "",
    // Set automatically when this dialog was opened from the no-invoice tab's
    // "Create Invoice" action. Passed through to the backend in the bill
    // payload so the bill skips stock-credit (stock was already credited at
    // entry submit) and the entry is flipped to INVOICED on success.
    //
    // For multi-entry handoffs, `linked_no_invoice_entry_ids` carries the
    // full ordered list of source entries. The singular field is mirrored
    // from the first id so existing input-lock checks keep working.
    linked_no_invoice_entry_id: null,
    linked_no_invoice_entry_ids: null,
    // Receiving Location (Feb 2026). Value is either the empty string
    // (= "billed-to branch", today's behaviour) or a vendor id (with
    // `is_job_worker=true`). Kept in sync with `branch_id` — picking a
    // new branch resets this to "" so the default remains "receive at
    // billed-to branch" unless the user deliberately overrides.
    receiving_vendor_id: "",
  });

  // Line Items State - now includes description, hsn, gst, account_id
  const [lineItems, setLineItems] = useState([
    { rm_id: "", rm_search: "", description: "", hsn: "", unit: "", quantity: 1, rate: 0, tax: "GST_18", tax_id: "", amount: 0, account_id: "" }
  ]);

  // Vendor invoice attachments — multiple files allowed (Feb 2026).
  // Uploaded sequentially AFTER bill creation so a storage hiccup doesn't
  // lose the bill. Backend appends to the bill's `attachments` array.
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const ALLOWED_INVOICE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf"];
  const MAX_INVOICE_BYTES = 5 * 1024 * 1024;

  // Manual LLM-backed invoice validation. WARN-ONLY — never blocks save.
  // `validationResult` is the mismatch summary returned by
  // POST /rm-inward/bills/validate-invoice (see services/invoice_validation.py).
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  // Totals State
  const [totals, setTotals] = useState({
    sub_total: 0,
    discount_type: "percentage", // "percentage" or "amount"
    discount_value: 0,
    discount_amount: 0,
    tds_tcs: "NONE",
    tds_tcs_amount: 0,
    tax_total: 0,
    grand_total: 0
  });

  // Sep 2026 — the page branch filter is independent of the global
  // BranchSelector and defaults to "ALL" branches (AC4, matching the
  // no-invoice tab). The old sync-from-global effect was removed:
  // zustand rehydration fires it a second time after mount, which
  // silently overrode the "ALL" default with the persisted global
  // branch.

  // Mount-only lookups (vendors, branches, RMs, Zoho config…).
  useEffect(() => {
    fetchAvailableRMs();
    fetchVendors();
    fetchBranches();
    fetchJobWorkerVendors();
    fetchZohoAccounts();
    fetchZohoTaxes();
    fetchTdsTaxes();
    fetchRmCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Branch-scoped inventory map follows the branch filter.
  useEffect(() => {
    fetchBranchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewBranch]);

  // Any filter change (status / branch / search / timeline) resets to
  // page 1 — never leave the user on "page 4 of 2".
  useEffect(() => { setPage(1); }, [viewBranch, statusFilter, searchText, timeline]);

  // Sep 2026 — the bills list is fully server-driven: pagination plus
  // every filter (status / branch / search / timeline) goes to the
  // backend, which filters over the complete collection. (The old
  // statusFilter-only refetch + include_deleted dance is gone —
  // "DELETED" is now just another server-side status value.)
  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewBranch, statusFilter, searchText, timeline, page, pageSize]);

  // When the parent shell hands us a `prefill` (from the no-invoice tab's
  // "Create Invoice" action), populate billData + lineItems and open the
  // dialog. The bill goes through the SAME create-bill flow as a normal new
  // bill, with `linked_no_invoice_entry_id` threaded into the payload so the
  // backend can mark the bill stock-already-credited and flip the linked
  // entry to INVOICED on success.
  useEffect(() => {
    if (!prefill) return;
    // Multi-entry support: when the no-invoice tab hands us aggregated data,
    // it sets `entry_ids` (array). Fall back to `entry_id` (single) for the
    // legacy one-entry flow. Both code paths converge here so the bill
    // payload always carries `linked_no_invoice_entry_ids` going forward.
    const multiEntryIds = Array.isArray(prefill.entry_ids) && prefill.entry_ids.length
      ? prefill.entry_ids
      : (prefill.entry_id ? [prefill.entry_id] : []);
    const firstEntryId = multiEntryIds[0] || null;
    setBillData((prev) => ({
      ...prev,
      vendor_id: prefill.vendor_id || "",
      vendor_name: prefill.vendor_name || "",
      branch_id: prefill.branch_id || "",
      // Bill # is deliberately NOT prefilled from the stores reference — the
      // user must enter the actual vendor invoice number here. The stores
      // reference is carried into Notes for traceability instead.
      bill_number: "",
      bill_date: prefill.entry_date ? prefill.entry_date.slice(0, 10) : prev.bill_date,
      notes: prefill.bill_number
        ? (prefill.notes
            ? `Stores ref: ${prefill.bill_number}\n${prefill.notes}`
            : `Stores ref: ${prefill.bill_number}`)
        : (prefill.notes || prev.notes),
      linked_no_invoice_entry_id: firstEntryId,
      linked_no_invoice_entry_ids: multiEntryIds.length > 0 ? multiEntryIds : null,
      // Surface the stores reference for the lock-hint UI without using it
      // as the actual Zoho bill number. For multi-entry, this is a comma-
      // separated string of every source entry's stores ref.
      linked_no_invoice_stores_ref: prefill.bill_number || null,
    }));
    // The vendor name input is driven by `vendorSearch`, not `billData.vendor_name` —
    // mirror the prefilled vendor name into it so the lock shows the right text.
    if (prefill.vendor_name) setVendorSearch(prefill.vendor_name);
    if (Array.isArray(prefill.line_items) && prefill.line_items.length > 0) {
      setLineItems(prefill.line_items.map((li) => ({
        rm_id: li.rm_id || "",
        rm_search: li.rm_id || "",
        description: asText(li.description),
        hsn: "",
        unit: li.unit || "",
        quantity: Number(li.quantity || 1),
        rate: 0,
        tax: "GST_18",
        tax_id: "",
        amount: 0,
        account_id: "",
      })));
    }
    setEditingBillId(null);
    setEditingDraft(false);
    setShowDialog(true);
    if (typeof onPrefillConsumed === "function") onPrefillConsumed();
  }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate totals when line items or discount changes
  // Recompute bill-level totals whenever line items, discount, or TDS change.
  // GST rule: tax must be applied to the NET taxable value (post-discount),
  // not the gross line amount. We treat the discount as a bill-level
  // discount and allocate it across lines pro-rata to each line's gross
  // amount, then compute tax on (gross - lineDiscountShare).
  useEffect(() => {
    const zohoLoaded = zohoTaxes && zohoTaxes.length > 0;

    // ---------- Pass 1: gross sub-total ----------
    let subTotal = 0;
    const lineGross = lineItems.map((item) => {
      const g = Number(item.quantity || 0) * Number(item.rate || 0);
      subTotal += g;
      return g;
    });

    // ---------- Bill-level discount ----------
    let discountAmount = 0;
    if (totals.discount_type === "percentage") {
      discountAmount = subTotal * (Number(totals.discount_value || 0) / 100);
    } else {
      discountAmount = Number(totals.discount_value || 0);
    }
    // Cap discount at the sub-total (avoid negative taxable value).
    if (discountAmount > subTotal) discountAmount = subTotal;
    if (discountAmount < 0) discountAmount = 0;

    // ---------- Pass 2: per-line tax on POST-discount amount ----------
    let taxTotal = 0;
    lineItems.forEach((item, idx) => {
      const gross = lineGross[idx];
      // Pro-rata share of the bill-level discount (zero if subTotal is zero).
      const lineDiscountShare = subTotal > 0 ? (gross / subTotal) * discountAmount : 0;
      const taxableAmount = gross - lineDiscountShare;

      let taxRate = 0;
      if (zohoLoaded) {
        if (item.tax_id) {
          taxRate = parseFloat(zohoTaxes.find((t) => t.tax_id === item.tax_id)?.tax_percentage) || 0;
        }
      } else {
        taxRate = TAX_OPTIONS.find((t) => t.value === item.tax)?.rate || 0;
      }
      taxTotal += taxableAmount * (taxRate / 100);
    });

    // ---------- TDS / TCS (rate × net taxable value) ----------
    const tdsRow = (totals.tds_tcs && totals.tds_tcs !== "NONE")
      ? tdsTaxes.find((t) => t.id === totals.tds_tcs)
      : null;
    const tdsTcsRate = tdsRow ? parseFloat(tdsRow.rate) || 0 : 0;
    const tdsTcsAmount = (subTotal - discountAmount) * (tdsTcsRate / 100);

    // ---------- Grand total ----------
    const grandTotal = subTotal - discountAmount + taxTotal - tdsTcsAmount;

    setTotals((prev) => ({
      ...prev,
      sub_total: subTotal,
      discount_amount: discountAmount,
      tds_tcs_amount: tdsTcsAmount,
      tax_total: taxTotal,
      grand_total: grandTotal,
    }));
  }, [lineItems, totals.discount_type, totals.discount_value, totals.tds_tcs, zohoTaxes, tdsTaxes]);

  // Auto-calculate due date when payment terms or bill date changes
  useEffect(() => {
    if (billData.payment_terms === "CUSTOM") return; // Don't auto-calculate for custom
    
    const paymentTerm = PAYMENT_TERMS.find(t => t.value === billData.payment_terms);
    if (paymentTerm && paymentTerm.days !== null && billData.bill_date) {
      const billDate = new Date(billData.bill_date);
      const dueDate = new Date(billDate);
      dueDate.setDate(dueDate.getDate() + paymentTerm.days);
      setBillData(prev => ({
        ...prev,
        due_date: dueDate.toISOString().split('T')[0]
      }));
    }
  }, [billData.payment_terms, billData.bill_date]);

  // Fetch Zoho accounts for expense/asset selection
  const fetchZohoAccounts = async () => {
    try {
      // First check if Zoho is configured
      const statusRes = await axios.get(`${API}/zoho/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZohoConfigured(statusRes.data.configured);
      
      if (statusRes.data.configured) {
        // Fetch expense accounts (most common for purchases)
        const response = await axios.get(`${API}/zoho/accounts?account_type=expense`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const accounts = response.data.accounts || [];
        setZohoAccounts(accounts);
        
        // Check for auth error
        if (response.data.auth_error) {
          setZohoAccountsError(response.data.message || "Authorization failed for Chart of Accounts");
          return;
        }
        
        if (accounts.length === 0 && response.data.message && response.data.message !== "OK") {
          setZohoAccountsError(response.data.message);
          return;
        }
        
        setZohoAccountsError("");

        // Pick default account with strict precedence:
        //  1. Exact "raw materials" (not "raw materials and consumables")
        //  2. Starts with "raw materials" but does NOT include "consumable"
        //  3. Any "raw material" substring
        //  4. Cost of Goods Sold / Purchases
        //  5. First account as last resort
        const norm = (s) => (s || "").toLowerCase().trim();
        const defaultAcc =
          accounts.find(a => norm(a.account_name) === "raw materials") ||
          accounts.find(a => norm(a.account_name).startsWith("raw materials") && !norm(a.account_name).includes("consumable")) ||
          accounts.find(a => norm(a.account_name).includes("raw material")) ||
          accounts.find(a => norm(a.account_name).includes("cost of goods") || norm(a.account_name).includes("purchases")) ||
          accounts[0];

        if (defaultAcc) {
          setDefaultAccountId(defaultAcc.account_id);
        }
      }
    } catch (error) {
      console.log("Zoho accounts fetch skipped:", error.message);
    }
  };

  const fetchZohoTaxes = async () => {
    try {
      if (!zohoConfigured) {
        // Wait for zoho status check — will be called after fetchZohoAccounts sets zohoConfigured
        const statusRes = await axios.get(`${API}/zoho/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!statusRes.data.configured) return;
      }
      const response = await axios.get(`${API}/zoho/taxes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const taxes = response.data.taxes || [];
      setZohoTaxes(taxes);
    } catch (error) {
      console.log("Zoho taxes fetch skipped:", error.message);
    }
  };

  const fetchTdsTaxes = async () => {
    try {
      const response = await axios.get(`${API}/tds-taxes?status=ACTIVE`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTdsTaxes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.log("TDS taxes fetch skipped:", error.message);
    }
  };

  const fetchRmCategories = async () => {
    try {
      const response = await axios.get(`${API}/rm-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // /api/rm-categories returns either an array or { code: cat } map. Normalise.
      const data = response.data;
      const list = Array.isArray(data) ? data : Object.values(data || {});
      setRmCategories(list);
    } catch (error) {
      console.log("RM categories fetch skipped:", error.message);
    }
  };

  // Look up the Zoho account expected for a given RM category. Falls back
  // to `null` when the category doesn't map or has no `zoho_account_type`.
  const lookupExpectedAccountForRm = (rmId) => {
    if (!rmId || !rmCategories.length || !zohoAccounts.length) return null;
    const rm = availableRMs.find(r => r.rm_id === rmId);
    if (!rm) return null;
    const cat = rmCategories.find(c =>
      (c.code || "").toUpperCase() === (rm.category || "").toUpperCase()
    );
    const expectedType = (cat?.zoho_account_type || "").trim();
    if (!expectedType) return null;
    // Find a Zoho account whose name matches the expected type
    // (case-insensitive, "Spares/Accessories" must match exactly).
    const norm = (s) => (s || "").toLowerCase().trim();
    const target = norm(expectedType);
    const acc =
      zohoAccounts.find(a => norm(a.account_name) === target) ||
      zohoAccounts.find(a => norm(a.account_name).includes(target));
    return acc
      ? { account_id: acc.account_id, account_name: acc.account_name, expected_type: expectedType, category_code: cat.code }
      : { account_id: null, account_name: null, expected_type: expectedType, category_code: cat.code };
  };



  const fetchBranchInventory = async () => {
    try {
      // "ALL" → skip the branch filter so RM inventory map covers every branch
      const branchParam = viewBranch && viewBranch !== "ALL" ? viewBranch : null;
      const url = branchParam
        ? `${API}/raw-materials?branch=${encodeURIComponent(branchParam)}`
        : `${API}/raw-materials`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }});
      const inventoryMap = {};
      response.data.forEach(rm => {
        inventoryMap[rm.rm_id] = rm.current_stock || 0;
      });
      setBranchInventory(inventoryMap);
    } catch (error) {
      console.error("Failed to fetch branch inventory");
    }
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      // Sep 2026 — every filter (status / branch / search / timeline)
      // plus pagination goes to the backend, which filters over the
      // complete collection. Status "DELETED" is now just another
      // server-side status value (the old include_deleted dance is
      // gone). `buildBillParams` is shared with the export path so the
      // two can never drift apart.
      const { from, to } = resolveDateRange(timeline);
      const params = {
        ...buildBillParams({
          status: statusFilter,
          branch: viewBranch,
          search: searchText,
          from,
          to,
        }),
        page,
        page_size: pageSize,
      };
      const response = await axios.get(`${API}/rm-inward/bills`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const bills = response.data?.bills || [];
      setTotal(typeof response.data?.total === "number" ? response.data.total : bills.length);
      if (response.data?.stats) setStats(response.data.stats);
      // One row per bill (bill-level view). Editing a draft bill to add a
      // line item must not produce a duplicate row in this table — the
      // detail dialog (View) is where line-item detail belongs.
      const rows = bills.map((b) => {
        const items = b.line_items || [];
        const totalQty = items.reduce((s, li) => s + Number(li.quantity || 0), 0);
        const totalAmount = (b.totals && Number(b.totals.grand_total))
          || items.reduce((s, li) => s + Number(li.amount || 0), 0);
        return {
          id: b.bill_id,
          bill_id: b.bill_id,
          bill_number: b.bill_number,
          bill_uuid: b.id,
          vendor_id: b.vendor_id,
          vendor_name: b.vendor_name,
          branch: b.branch,
          branch_id: b.branch_id,
          order_number: b.order_number,
          bill_date: b.bill_date,
          due_date: b.due_date,
          payment_terms: b.payment_terms,
          accounts_payable: b.accounts_payable,
          reverse_charge: b.reverse_charge,
          notes: b.notes,
          line_items: b.line_items || [],
          totals: b.totals || {},
          item_count: items.length,
          first_rm_id: items[0]?.rm_id || null,
          total_quantity: totalQty,
          total_amount: totalAmount,
          date: b.bill_date || b.created_at,
          status: b.status || "APPROVED",  // legacy bills default to APPROVED
          zoho_bill_status: b.zoho_bill_status || null,
          zoho_push_status: b.zoho_push_status || null,
          zoho_push_error: b.zoho_push_error || null,
          zoho_push_retry_count: b.zoho_push_retry_count || 0,
          // Feb 2026 — carry persisted attachments through the list row
          // so the pencil-edit affordance can hydrate them into the
          // edit dialog's read-only "Already on this bill" panel.
          attachments: b.attachments || [],
        };
      });
      setEntries(rows);
    } catch (error) {
      toast.error("Failed to fetch inward entries");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableRMs = async () => {
    try {
      const response = await axios.get(
        `${API}/raw-materials`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setAvailableRMs(response.data);
      setFilteredRMs(response.data.slice(0, 100));
    } catch (error) {
      toast.error("Failed to fetch available RMs");
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, { headers: { Authorization: `Bearer ${token}` }});
      setVendors(response.data.vendors || response.data || []);
    } catch (error) {
      console.error("Failed to fetch vendors");
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches/reference`, { headers: { Authorization: `Bearer ${token}` }});
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error("Failed to fetch branches");
    }
  };

  // Job-work vendors for the Receiving Location picker. Reuses the
  // existing `GET /api/vendors?is_job_worker=true` filter — same
  // endpoint the DC dispatch dialog uses.
  const fetchJobWorkerVendors = async () => {
    try {
      const res = await axios.get(
        `${API}/vendors?is_job_worker=true`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const list = res.data?.vendors || res.data || [];
      setJobWorkerVendors(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to fetch job-work vendors");
    }
  };

  const filterRMsForLine = (search) =>
    filterRMsForSearch(search, availableRMs, { includeFreight: true });

  // Filter vendors based on search
  const filteredVendors = vendorSearch.length >= 1
    ? vendors.filter(v => 
        v.name.toLowerCase().includes(vendorSearch.toLowerCase()) ||
        (v.vendor_code || "").toLowerCase().includes(vendorSearch.toLowerCase())
      )
    : vendors;

  // Get RM description. Uses ONLY the pre-composed `description` field on
  // the RM doc (e.g. "Seat - Pixi - Type 1 - Black"). If the field is
  // missing/blank, the line shows no description — by design, so users
  // know to go fix it in RM Repository. No silent fallback to category
  // pieces, no toast prompt.
  const getRMDescription = (rm) => {
    if (!rm) return "";
    if (rm.is_freight || rm.rm_id === "FREIGHT") return "Freight (service charge)";
    return rm.description || "";
  };

  // Get default GST for RM based on category
  const getDefaultGST = (rm) => {
    if (!rm) return "GST_18";
    // Default GST mapping by category
    const gstMapping = {
      "INP": "GST_18",
      "INM": "GST_18", 
      "ACC": "GST_18",
      "ELC": "GST_18",
      "LB": "GST_12",
      "PM": "GST_12",
      "BS": "GST_5",
      "SP": "GST_18"
    };
    return rm.hsn_gst || gstMapping[rm.category] || "GST_18";
  };

  // Resolve a Zoho tax_id whose tax_percentage matches the local tax code
  // (e.g., "GST_5" → 5% → first Zoho tax with tax_percentage===5).
  //
  // PREFER tax_type==='tax_group' over flat 'tax' when both exist at the same
  // percentage. Indian Zoho ships GST percentages in two flavours:
  //   - flat tax (e.g. "GST 18%") — single line item, fails for intra-state
  //     bills with "Both CGST and SGST has to be applied" 502 error.
  //   - tax_group (e.g. "GST18") — automatically splits into CGST + SGST
  //     (each 50% of the rate). This is what Zoho expects for intra-state.
  //
  // Returns "" when zohoTaxes is not yet loaded or no match exists.
  const resolveTaxIdFromCode = (taxCode) => {
    if (!zohoTaxes || zohoTaxes.length === 0) return "";
    const rate = LOCAL_TAX_RATES[taxCode];
    if (rate == null || rate === 0) return "";
    const matches = zohoTaxes.filter(
      t => Math.abs(parseFloat(t.tax_percentage) - rate) < 0.001
    );
    if (!matches.length) return "";
    // Prefer tax_group (CGST+SGST split) — Zoho needs it for intra-state.
    const group = matches.find(t => t.tax_type === "tax_group");
    return (group || matches[0]).tax_id || "";
  };

  // Once Zoho taxes load, back-fill tax_id on any line that has tax set but no
  // tax_id yet — keeps the Select & total-calc in sync with a single source of
  // truth (item.tax_id) without a UI fallback that silently masks desync.
  useEffect(() => {
    if (!zohoTaxes || zohoTaxes.length === 0) return;
    setLineItems(prev => {
      let dirty = false;
      const next = prev.map(item => {
        if (!item.tax_id && item.tax) {
          const resolved = resolveTaxIdFromCode(item.tax);
          if (resolved) {
            dirty = true;
            return { ...item, tax_id: resolved };
          }
        }
        return item;
      });
      return dirty ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zohoTaxes]);

  // Get HSN code for RM
  const getHSNCode = (rm) => {
    if (!rm) return "";
    // Default HSN mapping by category
    const hsnMapping = {
      "INP": "3926",
      "INM": "7326",
      "ACC": "8714",
      "ELC": "8544",
      "LB": "4821",
      "PM": "4819",
      "BS": "4911",
      "SP": "8714"
    };
    return rm.hsn_code || hsnMapping[rm.category] || "";
  };

  // Fetch latest invoice rate from rm_prices_history (vendor-agnostic) and
  // splice it into the given line. Used by BOTH the dropdown handler
  // (handleRMSelect) and the search-input handler (handleLineItemChange's
  // rm_search branch). Safe against row swaps via an rmId guard.
  const fetchLatestPriceForLine = (index, rmId) => {
    if (!rmId) return;
    axios
      .get(`${API}/rm-prices/latest/${encodeURIComponent(rmId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const price = parseFloat(res.data?.price_per_unit) || 0;
        setLineItems((prev) => {
          if (prev[index]?.rm_id !== rmId) return prev;
          const next = [...prev];
          next[index] = {
            ...next[index],
            rate: price,
            amount: (next[index].quantity || 0) * price,
          };
          return next;
        });
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          toast.warning(`No price on record for ${rmId} — enter the rate manually.`);
        } else {
          toast.error(`Could not fetch rate for ${rmId}: ${err.response?.data?.detail || err.message}`);
        }
      });
  };

  // Resolve the Zoho "Freight Inwards" GL account_id from the loaded
  // zohoAccounts (expense list). Case/punctuation-tolerant — matches
  // "Freight Inwards", "Freight-Inwards", "Freight Inwards A/c", etc.
  const findFreightInwardsAccountId = () => {
    if (!zohoAccounts || zohoAccounts.length === 0) return "";
    const norm = (s) => (s || "").toLowerCase().replace(/[-_]/g, " ").replace(/\s+/g, " ").trim();
    const target = "freight inwards";
    const exact = zohoAccounts.find((a) => norm(a.account_name) === target);
    if (exact) return exact.account_id;
    const partial = zohoAccounts.find((a) => norm(a.account_name).includes(target));
    return partial ? partial.account_id : "";
  };

  // Handle RM selection - auto-populate description, HSN, GST, unit, and rate (from price history)
  const handleRMSelect = (index, rmId) => {
    // FREIGHT: synthetic line — qty locked at 1, no price history fetch.
    // Auto-route the GL account to "Freight Inwards" so finance doesn't
    // have to set it manually on every bill.
    if (rmId === "FREIGHT") {
      const taxCode = "GST_18";
      const freightAccId = findFreightInwardsAccountId();
      const updated = [...lineItems];
      updated[index] = {
        ...updated[index],
        rm_id: "FREIGHT",
        rm_search: "FREIGHT",
        description: "Freight (service charge)",
        hsn: "",
        tax: taxCode,
        tax_id: resolveTaxIdFromCode(taxCode),
        unit: "",
        quantity: 1,
        rate: 0,
        amount: 0,
        account_id: freightAccId,
        is_freight: true,
      };
      setLineItems(updated);
      if (zohoConfigured && !freightAccId) {
        toast.warning(
          'Could not auto-select "Freight Inwards" account — create it in Zoho Books, or pick an account manually.'
        );
      }
      return;
    }
    const rm = availableRMs.find(r => r.rm_id === rmId);
    const taxCode = getDefaultGST(rm);
    const updated = [...lineItems];
    updated[index] = {
      ...updated[index],
      rm_id: rmId,
      rm_search: rmId,
      description: getRMDescription(rm),
      hsn: getHSNCode(rm),
      tax: taxCode,
      tax_id: resolveTaxIdFromCode(taxCode),
      unit: rm?.resolved_uom || "",
      rate: 0,     // reset before async fetch so the user sees 0 while loading
      amount: 0,
      is_freight: false,
    };
    // Auto-fill the Zoho GL account from the RM's category mapping.
    const expected = lookupExpectedAccountForRm(rmId);
    if (expected?.account_id) {
      updated[index].account_id = expected.account_id;
    } else if (expected && !expected.account_id) {
      toast.warning(
        `No Zoho account named "${expected.expected_type}" found. Please create it in Zoho or pick one manually.`
      );
    }
    setLineItems(updated);
    // Async fetch latest invoice price.
    fetchLatestPriceForLine(index, rmId);
  };

  const handleAddLineItem = () => {
    const taxCode = "GST_18";
    setLineItems([...lineItems, {
      rm_id: "", rm_search: "", description: "", hsn: "", unit: "",
      quantity: 1, rate: 0,
      tax: taxCode, tax_id: resolveTaxIdFromCode(taxCode),
      amount: 0, account_id: defaultAccountId || ""
    }]);
  };

  const handleRemoveLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    // Auto-calculate amount when quantity or rate changes
    if (field === "quantity" || field === "rate") {
      updated[index].amount = updated[index].quantity * updated[index].rate;
    }
    
    // When user types in rm_search, try to auto-select the RM
    if (field === "rm_search" && value) {
      const resolved = resolveTypedRM(value, availableRMs, { includeFreight: true });
      if (resolved && resolved.is_freight) {
        // Special-case: typing "FREIGHT" exactly selects the synthetic
        // freight line (it doesn't match the RM-id regex below).
        const taxCode = "GST_18";
        const freightAccId = findFreightInwardsAccountId();
        updated[index].rm_id = "FREIGHT";
        updated[index].description = "Freight (service charge)";
        updated[index].hsn = "";
        updated[index].tax = taxCode;
        updated[index].tax_id = resolveTaxIdFromCode(taxCode);
        updated[index].unit = "";
        updated[index].quantity = 1;
        updated[index].rate = 0;
        updated[index].amount = 0;
        updated[index].account_id = freightAccId;
        updated[index].is_freight = true;
        setLineItems(updated);
        if (zohoConfigured && !freightAccId) {
          toast.warning(
            'Could not auto-select "Freight Inwards" account — create it in Zoho Books, or pick an account manually.'
          );
        }
        return;
      }
      if (resolved) {
        const rmId = resolved.rm_id;
        const rm = resolved;
        const taxCode = getDefaultGST(rm);
        updated[index].rm_id = rmId;
        updated[index].description = getRMDescription(rm);
        updated[index].hsn = getHSNCode(rm);
        updated[index].tax = taxCode;
        updated[index].tax_id = resolveTaxIdFromCode(taxCode);
        updated[index].unit = rm?.resolved_uom || "";
        // Reset rate; will be overwritten by the async price-history fetch below.
        updated[index].rate = 0;
        updated[index].amount = 0;
        // Auto-fill GL account from the RM's category mapping
        const expected = lookupExpectedAccountForRm(rmId);
        if (expected?.account_id) {
          updated[index].account_id = expected.account_id;
        }
        // Fire-and-forget: auto-fill rate from latest rm_prices_history row.
        // Vendor-agnostic — picks the absolute latest invoice for this RM.
        fetchLatestPriceForLine(index, rmId);
      }
    }

    // When user changes the account manually, warn if it doesn't match the
    // category's expected `zoho_account_type` — but still allow the override.
    if (field === "account_id" && value && updated[index].rm_id) {
      const expected = lookupExpectedAccountForRm(updated[index].rm_id);
      if (expected?.expected_type) {
        const chosen = zohoAccounts.find(a => a.account_id === value);
        const norm = (s) => (s || "").toLowerCase().trim();
        const matches =
          chosen && (
            norm(chosen.account_name) === norm(expected.expected_type) ||
            norm(chosen.account_name).includes(norm(expected.expected_type))
          );
        if (!matches) {
          toast.error(
            `This is not the right account type for this category. Expected "${expected.expected_type}" (category ${expected.category_code}).`
          );
        }
      }
    }
    
    setLineItems(updated);
  };

  const handleSubmit = async () => {
    // Validation
    if (!billData.vendor_id) {
      toast.error("Please select a vendor");
      return;
    }
    if (!billData.bill_number) {
      toast.error("Please enter bill number");
      return;
    }
    if (!billData.bill_date) {
      toast.error("Please enter bill date");
      return;
    }
    // Jul 2026 — same fix as handleSaveDraft. Don't fall back to the
    // page-level `selectedBranch` filter for the bill's own Branch.
    // This path pushes straight to Zoho, so a silent substitution
    // cascades into stock inward once approved.
    if (!billData.branch_id) {
      toast.error("Please select a branch for this bill.");
      return;
    }
    
    // Typed-but-not-picked guard. Mirrors Dispatch.js / IBT.js fix: if a
    // user types/pastes an RM code directly into the search input without
    // clicking a dropdown suggestion, `rm_id` stays empty in React state
    // even though the input visually shows the code. Detect the row and
    // tell the user to pick from the dropdown.
    const typedNotPicked = lineItems.findIndex(
      (item) => !item.rm_id && (Number(item.quantity) > 0 || Number(item.rate) > 0 || item.tax_id)
    );
    if (typedNotPicked !== -1) {
      toast.error(
        `Row ${typedNotPicked + 1}: click an option from the RM search dropdown — typed text alone isn't a selection.`
      );
      return;
    }

    // Feb 2026 — resolved-picker guard. Independently of the
    // typed-not-picked check above, every row with an rm_id must
    // still resolve against the current RM Repository (`availableRMs`).
    // The prior guard only fires when `rm_id` is empty; a stale value
    // (e.g. a category label like "Consumables ( Kg )" copy-pasted
    // into rm_id via legacy code paths) would sneak past. This closes
    // the exact shape that produced `BILL_202608_01222` on prod.
    const unresolvedRow = lineItems.findIndex(
      (item) => item.rm_id
        && item.rm_id !== "FREIGHT"
        && !availableRMs.find(r => r.rm_id === item.rm_id)
    );
    if (unresolvedRow !== -1) {
      toast.error(
        `Row ${unresolvedRow + 1} rm_id="${lineItems[unresolvedRow].rm_id}" doesn't match any RM in the Repository — pick from the dropdown before saving.`
      );
      return;
    }

    const validItems = lineItems.filter(item => item.rm_id && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error("Please add at least one valid line item (select RM ID from dropdown)");
      return;
    }

    // When Zoho is configured, tax_id is the source of truth. Block the
    // submit if any line is missing one — prevents the silent 5% / 18%
    // mismatch where the dropdown shows a tax via a fallback while the
    // line item state holds none.
    const zohoLoaded = zohoTaxes && zohoTaxes.length > 0;
    if (zohoLoaded) {
      const missing = validItems.find(item => !item.tax_id);
      if (missing) {
        toast.error(`Pick a tax for line "${missing.rm_id}" before saving the bill.`);
        return;
      }
    }

    try {
      setSubmitting(true);
      toast.info("Creating bill...", { duration: 10000 });
      
      const payload = {
        ...billData,
        branch: branches.find(b => b.branch_id === billData.branch_id)?.name,
        branch_id: billData.branch_id,
        // Empty string means "billed-to branch" — send null so backend
        // treats it as the default receiving location.
        receiving_vendor_id: billData.receiving_vendor_id || null,
        line_items: validItems.map(item => {
          const taxPct = zohoLoaded
            ? (parseFloat(zohoTaxes.find(t => t.tax_id === item.tax_id)?.tax_percentage) || 0)
            : (TAX_OPTIONS.find(t => t.value === item.tax)?.rate || 0);
          return {
            rm_id: item.rm_id,
            description: asText(item.description),
            hsn: asText(item.hsn),
            unit: item.unit,
            quantity: parseFloat(item.quantity),
            rate: parseFloat(item.rate),
            tax: item.tax,
            tax_id: item.tax_id || undefined,
            tax_amount: item.amount * (taxPct / 100),
            amount: item.amount,
            account_id: item.account_id || defaultAccountId || undefined,
          };
        }),
        totals: {
          sub_total: totals.sub_total,
          discount_type: totals.discount_type,
          discount_value: totals.discount_value,
          discount_amount: totals.discount_amount,
          tds_tcs: totals.tds_tcs,
          tds_tcs_amount: totals.tds_tcs_amount,
          tax_total: totals.tax_total,
          grand_total: totals.grand_total
        },
        date: new Date(billData.bill_date).toISOString()
      };

      const response = editingDraft && editingBillId
        ? await (async () => {
            // Feb 2026 — Reopened PLAYFIELD_DRAFT + Submit MUST push
            // to Zoho (previously silently went to `/draft`, which
            // only saved locally). Two-step: PUT /draft to persist
            // the edits first, then POST /submit — same sequence the
            // row-level "Submit to Zoho" action already uses. Any
            // error at either step surfaces exactly like the row
            // action's error path.
            await axios.put(`${API}/rm-inward/bills/${encodeURIComponent(editingBillId)}/draft`, payload, {
              headers: { Authorization: `Bearer ${token}` },
            });
            return await axios.post(`${API}/rm-inward/bills/${encodeURIComponent(editingBillId)}/submit`, {}, {
              headers: { Authorization: `Bearer ${token}` },
            });
          })()
        : editingBillId
        ? await axios.put(`${API}/rm-inward/bills/${encodeURIComponent(editingBillId)}`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          })
        : await axios.post(`${API}/rm-inward/bills`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });

      // Jul 2026 — Zoho permanent-4xx no longer destroys the bill.
      // Backend returns HTTP 200 with { saved_as_draft: true, ... } and
      // preserves the row as PLAYFIELD_DRAFT so the user can edit + retry.
      // Surface a non-destructive info toast instead of the old error path.
      if (response?.data?.saved_as_draft) {
        const zohoErr = response.data.zoho_error || {};
        toast.warning(
          `Zoho rejected: ${zohoErr.message || "unknown error"}. ` +
          `Bill saved as Playfield Draft — edit and retry.`,
          { duration: 12000 }
        );
        setShowDialog(false);
        resetForm();
        fetchEntries();
        return;
      }

      // Feb 2026: bill create now uses the Zoho push orchestrator. When the
      // remote call exceeds 8s the backend returns `{status:"pending", job_id}`
      // and finishes the push in the background. Close the dialog, surface a
      // "saving in background" toast, poll up to 60s, then toast the final
      // outcome (or "check later" on timeout).
      if (!editingBillId && response?.data?.status === "pending" && response?.data?.job_id) {
        const jobId = response.data.job_id;
        const billNumber = billData.bill_number;
        toast.success(
          response.data.message ||
            `Bill ${billNumber} is being pushed to Zoho in the background…`,
          { duration: 6000 }
        );
        setShowDialog(false);
        resetForm();
        fetchEntries();
        fetchBranchInventory();
        try {
          const final = await pollJobUntilDone(jobId);
          if (final.status === "COMPLETED") {
            const r = final.result || {};
            toast.success(
              r.zoho_bill_id
                ? `Bill ${billNumber} pushed to Zoho (id ${r.zoho_bill_id}). Approve in Zoho to inward stock.`
                : `Bill ${billNumber} pushed to Zoho.`,
              { duration: 6000 }
            );
          } else {
            toast.error(
              `Zoho push failed for ${billNumber}: ${final.error || "unknown error"}`,
              { duration: 12000 }
            );
          }
        } catch (pollErr) {
          if (pollErr?.reason === "timeout") {
            toast.message(
              `Still pushing ${billNumber} to Zoho — check back in a minute. The bill list will refresh automatically.`,
              { duration: 8000 }
            );
          } else {
            toast.error(`Could not track Zoho push: ${pollErr.message || pollErr}`, { duration: 8000 });
          }
        } finally {
          fetchEntries();
          fetchBranchInventory();
          setSubmitting(false);
        }
        return;
      }

      if (editingBillId) {
        toast.success(`Bill ${editingBillId} updated and synced to Zoho.`, { duration: 5000 });
      } else {
        toast.success(
          `Bill ${billData.bill_number} created as DRAFT. Stock will inward once the bill is approved (status=open) in Zoho Books.`,
          { duration: 6000 }
        );
      }

      // After bill creation: upload any vendor-invoice files the user
      // selected. Multiple files (Feb 2026) — each one POSTed individually
      // so a single failure doesn't abort the rest. Backend appends each
      // file to the bill's `attachments` array.
      const newBillId = response.data?.bill?.bill_id || response.data?.bill_id;
      if (invoiceFiles.length > 0 && newBillId) {
        let okCount = 0;
        let failCount = 0;
        let zohoSyncedCount = 0;
        const failMsgs = [];
        for (const f of invoiceFiles) {
          try {
            const fd = new FormData();
            fd.append("file", f);
            const upResp = await axios.post(
              `${API}/rm-inward/bills/${newBillId}/attachment`,
              fd,
              { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );
            okCount += 1;
            if (upResp.data?.zoho?.zoho_synced) zohoSyncedCount += 1;
          } catch (uploadErr) {
            failCount += 1;
            failMsgs.push(`${f.name}: ${uploadErr.response?.data?.detail || "upload failed"}`);
          }
        }
        if (okCount > 0 && failCount === 0) {
          toast.success(
            zohoSyncedCount === okCount
              ? `${okCount} vendor invoice file(s) attached and synced to Zoho.`
              : `${okCount} vendor invoice file(s) attached locally${zohoSyncedCount ? ` (${zohoSyncedCount} synced to Zoho)` : ""}.`,
            { duration: 5000 }
          );
        } else if (okCount > 0 && failCount > 0) {
          toast.warning(
            `${okCount} file(s) attached; ${failCount} failed: ${failMsgs.join("; ")}`,
            { duration: 8000 }
          );
        } else if (failCount > 0) {
          toast.error(`Bill saved, but file uploads failed: ${failMsgs.join("; ")}`, { duration: 10000 });
        }
      }

      setShowDialog(false);
      resetForm();
      fetchEntries();
      fetchBranchInventory();
    } catch (error) {
      const { toast: msg } = formatApiError(error, "Bill not recorded");
      toast.error(msg, { duration: 12000 });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setBillData({
      vendor_id: "",
      vendor_name: "",
      branch_id: "",
      bill_number: "",
      order_number: "",
      bill_date: new Date().toISOString().split('T')[0],
      due_date: "",
      payment_terms: "DUE_ON_RECEIPT",
      accounts_payable: "Trade Payables",
      reverse_charge: false,
      notes: "",
      attachments: [],
    });
    setLineItems([{
      rm_id: "", rm_search: "", description: "", hsn: "", unit: "",
      quantity: 1, rate: 0,
      tax: "GST_18", tax_id: resolveTaxIdFromCode("GST_18"),
      amount: 0, account_id: ""
    }]);
    setInvoiceFiles([]);
    setValidationResult(null);
    setVendorSearch("");
    setTotals({
      sub_total: 0,
      discount_type: "percentage",
      discount_value: 0,
      discount_amount: 0,
      tds_tcs: "NONE",
      tds_tcs_amount: 0,
      tax_total: 0,
      grand_total: 0
    });
    setEditingBillId(null);
    setEditingDraft(false);
  };

  // Manual LLM-backed invoice validation. WARN-ONLY — never blocks save.
  // Posts the FIRST selected file + current form state to
  // POST /rm-inward/bills/validate-invoice and renders a mismatch summary
  // panel. Backend keeps NO raw LLM payload, only the summary.
  const handleValidateInvoice = async () => {
    const invoiceFile = invoiceFiles[0];
    if (!invoiceFile) {
      toast.error("Attach a vendor invoice file first");
      return;
    }
    // Resolve vendor GST from the selected vendor (for header GSTIN match).
    const vendor = vendors.find((v) => v.vendor_id === billData.vendor_id);
    const formPayload = {
      bill_number: billData.bill_number,
      bill_date: billData.bill_date,
      due_date: billData.due_date,
      vendor_name: billData.vendor_name,
      vendor_gstin: vendor?.gst || "",
      line_items: lineItems.map((li) => {
        const ratePct = LOCAL_TAX_RATES[li.tax] ?? null;
        return {
          rm_id: li.rm_id,
          description: asText(li.description) || li.rm_search || li.rm_id,
          hsn: asText(li.hsn),
          quantity: Number(li.quantity) || 0,
          rate: Number(li.rate) || 0,
          amount: Number(li.amount) || 0,
          tax_percentage: ratePct,
        };
      }),
      totals: {
        sub_total: Number(totals.sub_total) || 0,
        discount_amount: Number(totals.discount_amount) || 0,
        tax_total: Number(totals.tax_total) || 0,
        grand_total: Number(totals.grand_total) || 0,
      },
    };

    setValidating(true);
    setValidationResult(null);
    try {
      const fd = new FormData();
      fd.append("file", invoiceFile);
      fd.append("form_json", JSON.stringify(formPayload));
      const resp = await axios.post(
        `${API}/rm-inward/bills/validate-invoice`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }, timeout: 90000 }
      );
      setValidationResult(resp.data);
      const n = resp.data?.mismatch_count || 0;
      if (n === 0) {
        toast.success("Invoice matches the form — no mismatches");
      } else {
        toast.warning(`Validation complete — ${n} mismatch${n === 1 ? "" : "es"} found`);
      }
    } catch (err) {
      const m = formatApiError(err) || "Validation failed";
      toast.error(`Validation failed: ${m}`);
    } finally {
      setValidating(false);
    }
  };


  // Open the existing Create dialog pre-populated in edit mode. Reuses the
  // entire form — only the submit handler diverges (PUT vs POST). Triggered
  // from the detail dialog's Edit button.
  const handleStartEdit = (b) => {
    if (!b) return;
    // Jul 2026 — a PLAYFIELD_DRAFT bill must be reopened through the
    // dedicated draft edit endpoint. Same form, different Save target.
    const isDraft = (b.status || "").toUpperCase() === "PLAYFIELD_DRAFT";
    _hydrateFormFromBill(b);
    // For PLAYFIELD_DRAFT bills the backend keys the /draft PUT by the
    // internal UUID (b.id), not the human bill_id. Use whichever we have.
    setEditingBillId(isDraft ? (b.bill_uuid || b.id || b.bill_id) : b.bill_id);
    setEditingDraft(isDraft);
    setShowDialog(true);
  };

  // Save-as-Playfield-Draft — no Zoho, no stock, no PUSH_PENDING.
  const handleSaveDraft = async () => {
    if (!billData.vendor_id) { toast.error("Please select a vendor"); return; }
    if (!billData.bill_number) { toast.error("Please enter bill number"); return; }
    // Jul 2026 — do NOT fall back to the page-level `selectedBranch`
    // filter for the bill's own Branch. Pre-fix, an empty
    // `billData.branch_id` was silently substituted here, so 14 of 15
    // open drafts across 11 vendors ended up with `branch_id: null`
    // and a branch name that reflected the user's filter at save-time,
    // not a deliberate choice. Reject the save instead.
    if (!billData.branch_id) {
      toast.error("Please select a branch for this bill.");
      return;
    }
    const validItems = lineItems.filter(i => i.rm_id && i.quantity > 0);
    if (validItems.length === 0) {
      toast.error("Add at least one valid line item before saving as draft.");
      return;
    }
    // Feb 2026 — resolved-picker guard on the draft path too. Prior
    // handler only checked `rm_id && quantity > 0`, which let a
    // category label sitting in rm_id (the exact `BILL_202608_01222`
    // shape) sneak through into a saved draft.
    const unresolvedDraftRow = validItems.findIndex(
      (item) => item.rm_id !== "FREIGHT"
        && !availableRMs.find(r => r.rm_id === item.rm_id)
    );
    if (unresolvedDraftRow !== -1) {
      toast.error(
        `Row ${unresolvedDraftRow + 1} rm_id="${validItems[unresolvedDraftRow].rm_id}" doesn't match any RM in the Repository — pick from the dropdown before saving as draft.`
      );
      return;
    }
    try {
      setSubmitting(true);
      const branchName = branches.find(b => b.branch_id === billData.branch_id)?.name;
      const payload = {
        ...billData,
        branch: branchName,
        branch_id: billData.branch_id,
        receiving_vendor_id: billData.receiving_vendor_id || null,
        line_items: validItems.map(item => ({
          rm_id: item.rm_id, description: asText(item.description), hsn: asText(item.hsn),
          unit: item.unit, quantity: parseFloat(item.quantity),
          rate: parseFloat(item.rate), tax: item.tax, tax_id: item.tax_id || undefined,
          tax_amount: item.amount * ((TAX_OPTIONS.find(t => t.value === item.tax)?.rate || 0) / 100),
          amount: item.amount,
        })),
        totals,
        date: new Date(billData.bill_date).toISOString(),
      };
      const url = editingDraft && editingBillId
        ? `${API}/rm-inward/bills/${encodeURIComponent(editingBillId)}/draft`
        : `${API}/rm-inward/bills/draft`;
      const method = editingDraft && editingBillId ? "put" : "post";
      await axios[method](url, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Bill saved as Playfield Draft. Push to Zoho when ready.", { duration: 5000 });
      setShowDialog(false);
      resetForm();
      fetchEntries();
    } catch (err) {
      const { toast: msg } = formatApiError(err, "Failed to save draft");
      toast.error(msg, { duration: 10000 });
    } finally {
      setSubmitting(false);
    }
  };

  // Submit a PLAYFIELD_DRAFT to Zoho (row-level action).
  const handleSubmitDraft = async (entry) => {
    if (!entry?.bill_uuid && !entry?.bill_id) return;
    const key = entry.bill_uuid || entry.bill_id;
    try {
      toast.info(`Submitting draft ${entry.bill_number || key} to Zoho…`, { duration: 6000 });
      const resp = await axios.post(
        `${API}/rm-inward/bills/${encodeURIComponent(key)}/submit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data?.saved_as_draft) {
        const zErr = resp.data.zoho_error || {};
        toast.warning(
          `Zoho rejected: ${zErr.message || "unknown"}. Still a Playfield Draft — edit and retry.`,
          { duration: 12000 }
        );
      } else if (resp.data?.status === "DRAFT" || resp.data?.status === "pending") {
        toast.success(
          `Bill ${entry.bill_number || key} pushed to Zoho as DRAFT. ` +
          `Stock will inward when approved in Zoho.`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Bill ${entry.bill_number || key} submitted.`);
      }
      fetchEntries();
    } catch (err) {
      const { toast: msg } = formatApiError(err, "Submit failed");
      toast.error(msg, { duration: 12000 });
    }
  };

  // Shared form hydration — extracted so both handleStartEdit and any
  // future paths (undo, recover) can seed the dialog from a stored bill.
  const _hydrateFormFromBill = (b) => {
    const items = (b.line_items || []).map((li) => ({
      rm_id: li.rm_id || "",
      rm_search: li.rm_id || "",
      description: asText(li.description),
      hsn: asText(li.hsn),
      unit: li.unit || "",
      quantity: li.quantity || 1,
      rate: li.rate || 0,
      tax: li.tax || "NONE",
      tax_id: li.tax_id || "",
      tax_amount: li.tax_amount || 0,
      amount: li.amount || 0,
      account_id: li.account_id || "",
    }));
    // Jul 2026 — legacy PLAYFIELD_DRAFT rows saved before the branch
    // validation fix have `branch_id: null` but `branch` (name) populated.
    // Resolve the missing id back from the branch name so the edit
    // dialog opens with the bill's ACTUAL branch pre-selected instead
    // of an empty field that would then silently substitute
    // `selectedBranch` on save. Exact match first, case-insensitive
    // fallback for hand-typed legacy names.
    let resolvedBranchId = b.branch_id || "";
    if (!resolvedBranchId && b.branch) {
      const nameMatch = branches.find((x) => x.name === b.branch)
        || branches.find((x) => (x.name || "").toLowerCase() === b.branch.toLowerCase());
      if (nameMatch) resolvedBranchId = nameMatch.branch_id;
    }
    setBillData({
      vendor_id: b.vendor_id || "",
      vendor_name: b.vendor_name || "",
      branch_id: resolvedBranchId,
      bill_number: b.bill_number || "",
      order_number: b.order_number || "",
      bill_date: b.bill_date || new Date().toISOString().split("T")[0],
      due_date: b.due_date || "",
      payment_terms: b.payment_terms || "DUE_ON_RECEIPT",
      accounts_payable: b.accounts_payable || "Trade Payables",
      reverse_charge: !!b.reverse_charge,
      notes: b.notes || "",
      // Preserve the no-invoice link on edit so the lock state on
      // vendor / branch / RM lines / quantities / add-line is re-applied.
      // Without this, opening a linked draft for edit would expose those
      // fields as editable and break the carry-from-entry guarantee.
      linked_no_invoice_entry_id: b.linked_no_invoice_entry_id || null,
      linked_no_invoice_entry_ids: b.linked_no_invoice_entry_ids || (b.linked_no_invoice_entry_id ? [b.linked_no_invoice_entry_id] : null),
      linked_no_invoice_stores_ref: b.linked_no_invoice_stores_ref || null,
      // Feb 2026 — carry the persisted attachments through so the edit
      // dialog can render them read-only alongside the new-file picker.
      attachments: Array.isArray(b.attachments) ? b.attachments : [],
    });
    // The vendor name input is driven by `vendorSearch`, not `billData.vendor_name`.
    // Sync it so the locked input displays the right value.
    if (b.vendor_name) setVendorSearch(b.vendor_name);
    setLineItems(items.length ? items : [{
      rm_id: "", rm_search: "", description: "", hsn: "", unit: "",
      quantity: 1, rate: 0, tax: "GST_18",
      tax_id: resolveTaxIdFromCode("GST_18"), amount: 0, account_id: "",
    }]);
    setTotals(b.totals || {
      sub_total: 0, discount_type: "percentage", discount_value: 0,
      discount_amount: 0, tds_tcs: "NONE", tds_tcs_amount: 0,
      tax_total: 0, grand_total: 0,
    });
  };

  const handleExport = async () => {
    // Sep 2026 — export the COMPLETE filtered set, fetched server-side
    // via a paged loop. Previously this serialized the in-memory list,
    // which the backend silently capped at the newest 500 bills.
    try {
      const { from, to } = resolveDateRange(timeline);
      const baseParams = buildBillParams({
        status: statusFilter,
        branch: viewBranch,
        search: searchText,
        from,
        to,
      });
      const allBills = await fetchAllRmInwardBills(API, token, baseParams);
      const ws = XLSX.utils.json_to_sheet(allBills.map(b => {
        const items = b.line_items || [];
        return {
          'Date': b.bill_date ? new Date(b.bill_date).toLocaleDateString() : '-',
          'Bill #': b.bill_number || '-',
          'Vendor': b.vendor_name || '-',
          'Branch': b.branch || '-',
          'Items': items.length,
          'Total Quantity': items.reduce((s, li) => s + Number(li.quantity || 0), 0),
          'Amount': (b.totals && Number(b.totals.grand_total))
            || items.reduce((s, li) => s + Number(li.amount || 0), 0),
          'Status': b.status || '-',
          'Zoho Status': b.zoho_bill_status || '-',
        };
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'RM Inward');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `rm_inward_${viewBranch || "ALL"}.xlsx`);
      toast.success(`Exported ${allBills.length} bills to Excel`);
    } catch (e) {
      toast.error(formatApiError(e, "Export failed"));
    }
  };

  const getCurrentStock = (rm_id) => {
    return branchInventory[rm_id] || 0;
  };

  return (
    <div className="p-6 md:p-8" data-testid="rm-inward-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase">RM Inward / Purchase Bill</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">
            Record incoming raw materials and vendor bills for {viewBranch === "ALL" ? "all branches" : viewBranch}
          </p>
        </div>
        <div className="flex gap-3 items-center">
          {/* Sep 2026 — the Branch filter moved into the filter bar
              below (single control bound to the same `viewBranch`). */}
          <Button 
            variant="secondary" 
            onClick={handleExport}
            data-testid="export-inward-btn"
            className="uppercase text-xs tracking-wide"
          >
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Export
          </Button>
          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <PermButton entity="RMInwardBill" action="CREATE" data-testid="add-inward-btn" className="uppercase text-xs tracking-wide">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                New Bill / Inward
              </PermButton>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" aria-describedby="purchase-bill-description">
              <DialogHeader>
                <DialogTitle className="font-bold uppercase flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {editingBillId ? `Edit Bill — ${editingBillId}` : "New Purchase Bill"}
                </DialogTitle>
                <p id="purchase-bill-description" className="sr-only">
                  Create a new purchase bill to record incoming raw materials and update inventory
                </p>
              </DialogHeader>
              
              {/* Bill Header Section */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-bold uppercase">Vendor Name *</Label>
                    <Input
                      value={vendorSearch}
                      onChange={(e) => {
                        setVendorSearch(e.target.value);
                        // Auto-select vendor if exact match found
                        const exactMatch = vendors.find(v => 
                          v.name.toLowerCase() === e.target.value.toLowerCase() ||
                          `${v.name} (${v.vendor_code || v.vendor_id})`.toLowerCase() === e.target.value.toLowerCase()
                        );
                        if (exactMatch) {
                          // Auto-populate payment_terms from vendor; fallback to DUE_ON_RECEIPT for legacy vendors
                          const vendorPaymentTerms = exactMatch.payment_terms || "DUE_ON_RECEIPT";
                          setBillData({
                            ...billData,
                            vendor_id: exactMatch.id,
                            vendor_name: exactMatch.name,
                            payment_terms: vendorPaymentTerms,
                          });
                        }
                      }}
                      placeholder="Type vendor name..."
                      className="font-mono text-sm"
                      list="vendor-datalist"
                      data-testid="vendor-search"
                      disabled={!!billData.linked_no_invoice_entry_id}
                    />
                    <datalist id="vendor-datalist">
                      {filteredVendors.slice(0, 100).map(v => (
                        <option key={v.id} value={v.name}>{v.vendor_code || v.vendor_id}</option>
                      ))}
                    </datalist>
                    {billData.vendor_id && (
                      <div className="text-xs text-green-600 mt-1">✓ {billData.vendor_name}</div>
                    )}
                    {vendorSearch && !billData.vendor_id && filteredVendors.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">{filteredVendors.length} vendors match</div>
                    )}
                    {/* Multi-address vendor — Phase 1 read-only display (Feb 2026) */}
                    {billData.vendor_id && (() => {
                      const selected = vendors.find((v) => v.id === billData.vendor_id || v.vendor_id === billData.vendor_id);
                      const addrs = selected?.billing_addresses || [];
                      if (!addrs.length) {
                        return (
                          <div
                            className="mt-2 rounded-md border border-dashed border-zinc-300 bg-zinc-50 p-2 text-[11px] text-zinc-500"
                            data-testid="vendor-billing-addresses-empty"
                          >
                            No billing addresses on file for this vendor.
                          </div>
                        );
                      }
                      return (
                        <div
                          className="mt-2 rounded-md border border-zinc-200 bg-zinc-50 p-2 space-y-1.5"
                          data-testid="vendor-billing-addresses"
                        >
                          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                            Billing addresses ({addrs.length})
                          </div>
                          {addrs.map((a, i) => {
                            const lineParts = [a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean);
                            return (
                              <div
                                key={a.address_id || i}
                                className="rounded border border-zinc-200 bg-white px-2 py-1.5 text-[11px] leading-snug"
                                data-testid={`vendor-billing-address-${i}`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-semibold text-zinc-700">
                                    {a.label || `Address ${i + 1}`}
                                  </span>
                                  {a.is_primary && (
                                    <span className="rounded-sm bg-emerald-100 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-emerald-700">
                                      Primary
                                    </span>
                                  )}
                                </div>
                                {lineParts.length > 0 && (
                                  <div className="text-zinc-600">{lineParts.join(", ")}</div>
                                )}
                                {a.gstin && (
                                  <div className="font-mono text-[10px] text-zinc-500">
                                    GSTIN: <span className="text-zinc-700">{a.gstin}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                  
                  <div>
                    <Label className="text-xs font-bold uppercase">Branch</Label>
                    <Select 
                      value={billData.branch_id || undefined} 
                      onValueChange={(v) => setBillData({...billData, branch_id: v, receiving_vendor_id: ""})}
                      disabled={!!billData.linked_no_invoice_entry_id}
                    >
                      <SelectTrigger data-testid="branch-select">
                        <SelectValue placeholder={selectedBranch} />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.filter(b => b.branch_id).map(b => (
                          <SelectItem key={b.branch_id} value={b.branch_id}>{b.name} ({b.branch_id})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* ── Receiving Location (Feb 2026) ─────────────────
                      Where the RM physically lands: the billed-to
                      branch (default) or a job-work vendor. Only these
                      two shapes are valid — a different branch as
                      receiving is deliberately NOT offered because the
                      backend schema can't represent it today. When a
                      vendor is picked, approval credits
                      `job_worker_stock` instead of the branch's
                      inventory. The billed-to branch (Zoho GL, purchase
                      entries) is unchanged either way. */}
                  <div>
                    <Label className="text-xs font-bold uppercase">Receiving Location</Label>
                    <Select
                      value={billData.receiving_vendor_id || "__BILLED_TO_BRANCH__"}
                      onValueChange={(v) => setBillData({
                        ...billData,
                        receiving_vendor_id: v === "__BILLED_TO_BRANCH__" ? "" : v,
                      })}
                      disabled={!billData.branch_id || !!billData.linked_no_invoice_entry_id}
                    >
                      <SelectTrigger data-testid="receiving-location-select">
                        <SelectValue placeholder={billData.branch_id
                          ? "Billed-to branch"
                          : "Pick a branch first"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__BILLED_TO_BRANCH__"
                          data-testid="receiving-location-branch">
                          Billed-to branch{" "}
                          <span className="text-zinc-500">
                            ({branches.find(b => b.branch_id === billData.branch_id)?.name || billData.branch_id})
                          </span>
                        </SelectItem>
                        {jobWorkerVendors.map(v => (
                          <SelectItem key={v.id} value={v.id}
                            data-testid={`receiving-location-vendor-${v.id}`}>
                            <span className="font-mono text-[11px] text-zinc-500 mr-1">Job Worker ·</span>
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {billData.receiving_vendor_id && (
                      <p className="text-[10px] text-amber-700 mt-1">
                        Stock will be credited to this vendor&apos;s job-work ledger, not to{" "}
                        {branches.find(b => b.branch_id === billData.branch_id)?.name || "the branch"}&apos;s inventory.
                      </p>
                    )}
                  </div>
                </div>

                {/* Middle Column */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-bold uppercase">Bill # *</Label>
                    <Input 
                      value={billData.bill_number}
                      onChange={(e) => setBillData({...billData, bill_number: e.target.value})}
                      placeholder="e.g., INV-2026-001"
                      data-testid="bill-number-input"
                      className="font-mono"
                    />
                    {billData.linked_no_invoice_entry_id && (
                      <p className="text-[10px] text-amber-700 mt-1">
                        {Array.isArray(billData.linked_no_invoice_entry_ids) && billData.linked_no_invoice_entry_ids.length > 1 ? (
                          <>
                            Linked to {billData.linked_no_invoice_entry_ids.length} no-invoice entries:{" "}
                            <code className="font-mono">{billData.linked_no_invoice_entry_ids.join(", ")}</code>
                          </>
                        ) : (
                          <>
                            Linked to no-invoice entry <code className="font-mono">{billData.linked_no_invoice_entry_id}</code>
                          </>
                        )}
                        {billData.linked_no_invoice_stores_ref && (
                          <>{" "}· stores ref <code className="font-mono">{billData.linked_no_invoice_stores_ref}</code></>
                        )}
                        . Enter the actual vendor invoice number above.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase">Order Number</Label>
                    <Input 
                      value={billData.order_number}
                      onChange={(e) => setBillData({...billData, order_number: e.target.value})}
                      placeholder="PO reference"
                      className="font-mono"
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-bold uppercase">Bill Date *</Label>
                    <Input 
                      type="date"
                      value={billData.bill_date}
                      onChange={(e) => setBillData({...billData, bill_date: e.target.value})}
                      data-testid="bill-date-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase">Due Date</Label>
                    <Input 
                      type="date"
                      value={billData.due_date}
                      onChange={(e) => setBillData({...billData, due_date: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Second Row - Payment Terms, Accounts Payable */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-xs font-bold uppercase">Payment Terms</Label>
                  <Select 
                    value={billData.payment_terms || undefined} 
                    onValueChange={(v) => setBillData({...billData, payment_terms: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select payment terms" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.filter(pt => pt.value).map(pt => (
                        <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase">Accounts Payable</Label>
                  <Select 
                    value={billData.accounts_payable || undefined} 
                    onValueChange={(v) => setBillData({...billData, accounts_payable: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Trade Payables">Trade Payables</SelectItem>
                      <SelectItem value="Sundry Creditors">Sundry Creditors</SelectItem>
                      <SelectItem value="Other Payables">Other Payables</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Line Items Table */}
              <div>
                {zohoConfigured && zohoAccountsError && (
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="zoho-accounts-warning">
                    <strong>Zoho Accounts Warning:</strong> {zohoAccountsError}
                    <br />
                    <span className="text-xs">You can still enter Account IDs manually, or re-authorize Zoho with the required scope.</span>
                  </div>
                )}
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-xs font-bold uppercase">Item Details</Label>
                  {/* Top-level "Add Line" intentionally removed — see the
                      inline button under the last filled row below. Keeps
                      the cursor near the data being entered and removes
                      the back-and-forth scroll. */}
                </div>
                
                <div className="border rounded-lg overflow-hidden overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="w-[240px]">Item Details</TableHead>
                        <TableHead className="w-[110px] text-right">Qty</TableHead>
                        <TableHead className="w-[70px]">Unit</TableHead>
                        <TableHead className="w-[110px] text-right">Rate</TableHead>
                        <TableHead className="w-[80px]">Tax</TableHead>
                        {zohoConfigured && <TableHead className="w-[180px]">Account</TableHead>}
                        <TableHead className="w-[100px] text-right">Amount</TableHead>
                        <TableHead className="w-[40px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lineItems.map((item, idx) => (
                        <React.Fragment key={idx}>
                        <TableRow className="align-top">
                          <TableCell className="p-2 align-top">
                            {/* Zoho-style: RM ID input on top row; description rendered as a full-width row below */}
                            <Input
                              value={item.rm_search || item.rm_id}
                              onChange={(e) => handleLineItemChange(idx, "rm_search", e.target.value)}
                              placeholder="Type RM ID..."
                              className="font-mono text-xs h-8 w-[160px]"
                              list={`rm-list-${idx}`}
                              disabled={!!billData.linked_no_invoice_entry_id}
                              readOnly={!!billData.linked_no_invoice_entry_id}
                            />
                            <datalist id={`rm-list-${idx}`}>
                              {filterRMsForLine(item.rm_search || "").map(rm => (
                                <option key={rm.rm_id} value={rm.rm_id}>{getRMDescription(rm)}</option>
                              ))}
                            </datalist>
                            {item.rm_search && !item.rm_id && (
                              <select
                                className="w-full text-xs border rounded px-1 py-1 bg-gray-50 mt-1"
                                value={item.rm_id}
                                onChange={(e) => handleRMSelect(idx, e.target.value)}
                              >
                                <option value="">Select from matches</option>
                                {filterRMsForLine(item.rm_search || "").map(rm => (
                                  <option key={rm.rm_id} value={rm.rm_id}>
                                    {rm.rm_id} - {getRMDescription(rm)}
                                  </option>
                                ))}
                              </select>
                            )}
                            {item.rm_id && (
                              <>
                                {item.is_freight || item.rm_id === "FREIGHT" ? (
                                  <div className="flex items-center gap-1.5 mt-1.5">
                                    <span className="bg-amber-100 text-amber-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">
                                      Service
                                    </span>
                                  </div>
                                ) : item.hsn ? (
                                  <div className="flex items-center gap-1.5 mt-1.5">
                                    <span className="bg-blue-100 text-blue-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">
                                      Goods
                                    </span>
                                    <span
                                      className="text-[11px] text-zinc-500"
                                      data-testid={`rm-line-${idx}-hsn`}
                                    >
                                      HSN Code: <span className="text-zinc-700 font-mono">{asText(item.hsn)}</span>
                                    </span>
                                  </div>
                                ) : null}
                              </>
                            )}
                          </TableCell>
                          <TableCell className="p-2 align-top">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.is_freight || item.rm_id === "FREIGHT" ? 1 : item.quantity}
                              onChange={(e) => handleLineItemChange(idx, "quantity", parseFloat(e.target.value) || 0)}
                              onWheel={(e) => e.currentTarget.blur()}
                              className="no-spinner text-right font-mono text-xs h-8"
                              disabled={item.is_freight || item.rm_id === "FREIGHT" || !!billData.linked_no_invoice_entry_id}
                              readOnly={item.is_freight || item.rm_id === "FREIGHT" || !!billData.linked_no_invoice_entry_id}
                              data-testid={`rm-line-${idx}-qty`}
                            />
                          </TableCell>
                          <TableCell className="p-2 align-top">
                            {item.is_freight || item.rm_id === "FREIGHT" ? (
                              <div
                                className="text-xs font-mono h-8 flex items-center px-2 rounded border bg-gray-50 text-gray-500 border-gray-200"
                                title="Freight has no unit"
                                data-testid={`rm-line-${idx}-unit`}
                              >
                                —
                              </div>
                            ) : (
                            <div
                              className={`text-xs font-mono h-8 flex items-center px-2 rounded border ${
                                item.unit
                                  ? "bg-gray-50 text-gray-700 border-gray-200"
                                  : "bg-red-50 text-red-600 border-red-200"
                              }`}
                              title={item.unit ? `Unit from RM repository` : "No unit — fix in RM Repository"}
                              data-testid={`rm-line-${idx}-unit`}
                            >
                              {item.unit || "—"}
                            </div>
                            )}
                          </TableCell>
                          <TableCell className="p-2 align-top">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) => handleLineItemChange(idx, "rate", parseFloat(e.target.value) || 0)}
                              onWheel={(e) => e.currentTarget.blur()}
                              className="no-spinner text-right font-mono text-xs h-8"
                              placeholder="0.00"
                            />
                          </TableCell>
                          <TableCell className="p-2 align-top">
                            {zohoTaxes.length > 0 ? (
                              <Select 
                                value={item.tax_id || ""} 
                                onValueChange={(v) => {
                                  const tax = zohoTaxes.find(t => t.tax_id === v);
                                  const updated = [...lineItems];
                                  updated[idx].tax_id = v;
                                  updated[idx].tax = tax?.tax_name || "";
                                  // Recalculate amount with tax rate
                                  const taxRate = parseFloat(tax?.tax_percentage) || 0;
                                  updated[idx].amount = updated[idx].quantity * updated[idx].rate;
                                  setLineItems(updated);
                                }}
                              >
                                <SelectTrigger className="h-8 text-xs" data-testid={`tax-select-${idx}`}>
                                  <SelectValue placeholder="Tax" />
                                </SelectTrigger>
                                <SelectContent>
                                  {zohoTaxes.map(t => (
                                    <SelectItem key={t.tax_id} value={t.tax_id}>
                                      {t.tax_name} ({t.tax_percentage}%)
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Select 
                                value={item.tax || undefined} 
                                onValueChange={(v) => handleLineItemChange(idx, "tax", v)}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Tax" />
                                </SelectTrigger>
                                <SelectContent>
                                  {TAX_OPTIONS.filter(t => t.value).map(t => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </TableCell>
                          {zohoConfigured && (
                            <TableCell className="p-2 align-top">
                              {zohoAccounts.length > 0 ? (
                                <Select
                                  value={item.account_id || defaultAccountId || undefined}
                                  onValueChange={(v) => handleLineItemChange(idx, "account_id", v)}
                                >
                                  <SelectTrigger
                                    className="min-h-[2.25rem] h-auto text-xs py-1.5 items-start [&>span]:whitespace-normal [&>span]:text-left [&>span]:leading-tight [&>span]:line-clamp-none"
                                    data-testid={`account-select-${idx}`}
                                  >
                                    <SelectValue placeholder="Select account" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {zohoAccounts.map(acc => (
                                      <SelectItem
                                        key={acc.account_id}
                                        value={acc.account_id}
                                        className="whitespace-normal"
                                      >
                                        {acc.account_name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  value={item.account_id || ""}
                                  onChange={(e) => handleLineItemChange(idx, "account_id", e.target.value)}
                                  placeholder="Account ID"
                                  className="text-xs h-8 font-mono"
                                  data-testid={`account-input-${idx}`}
                                />
                              )}
                            </TableCell>
                          )}
                          <TableCell className="p-2 align-top text-right font-mono text-sm font-medium">
                            ₹{item.amount.toFixed(2)}
                          </TableCell>
                          <TableCell className="p-2 align-top">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleRemoveLineItem(idx)}
                              disabled={lineItems.length === 1 || !!billData.linked_no_invoice_entry_id}
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        {/* Description row — appears immediately below the
                            line item, spanning all columns. Only rendered
                            once an RM has been selected and a description
                            resolved (freight included). */}
                        {item.rm_id && item.description && (
                          <TableRow className="hover:bg-transparent border-t-0">
                            <TableCell
                              colSpan={zohoConfigured ? 8 : 7}
                              className="pt-0 pb-2 px-2 text-xs text-zinc-600 italic whitespace-normal break-words"
                              data-testid={`rm-line-${idx}-description`}
                            >
                              <span className="text-[10px] uppercase tracking-wider text-zinc-400 mr-1.5">Description:</span>
                              {asText(item.description)}
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      ))}
                      {/* Inline "Add Line" row — appears under the LAST line
                          item only after the user has picked an RM on it.
                          Keeps the action next to the data being entered and
                          guarantees at most one Add Line affordance is ever
                          visible (matches the user spec May 19, 2026). */}
                      {(() => {
                        const last = lineItems[lineItems.length - 1];
                        const ready = !!(last && last.rm_id);
                        if (!ready) return null;
                        // When this dialog was opened via the no-invoice "Create Invoice"
                        // handoff, the line items (and their RM IDs / quantities) are
                        // locked to the source entry. No new RM rows can be added.
                        if (billData.linked_no_invoice_entry_id) return null;
                        const colSpan = zohoConfigured ? 8 : 7;
                        return (
                          <TableRow className="bg-zinc-50 hover:bg-zinc-100" data-testid="rm-inward-inline-add-line-row">
                            <TableCell colSpan={colSpan} className="p-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleAddLineItem}
                                className="h-7 text-xs text-blue-700 hover:text-blue-900"
                                data-testid="rm-inward-inline-add-line-btn"
                              >
                                <Plus className="h-3 w-3 mr-1" /> Add another line
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Totals Section */}
              <div className="grid grid-cols-2 gap-8">
                {/* Notes + Invoice attachment */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-bold uppercase">Notes / Remarks</Label>
                    <Textarea 
                      value={billData.notes}
                      onChange={(e) => setBillData({...billData, notes: e.target.value})}
                      placeholder="Additional notes, truck details, GRN reference..."
                      rows={4}
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-bold uppercase">Vendor Invoice Files (Image / PDF, optional)</Label>
                    {/* Feb 2026 — Show already-persisted attachments on this
                        bill (edit mode only). Read-only rendering mirrors
                        RMInwardBillDetailDialog so users see what's already
                        attached before adding more. Hidden entirely on new
                        bills or when the bill has no non-deleted attachments,
                        so today's "picker only" experience is preserved for
                        those flows. */}
                    {editingBillId && (() => {
                      const existing = (billData.attachments || []).filter(a => !a?.is_deleted);
                      if (existing.length === 0) return null;
                      return (
                        <div
                          className="mt-1 mb-2 border rounded-md bg-zinc-50 px-3 py-2"
                          data-testid="rm-bill-existing-attachments"
                        >
                          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                            Already on this bill ({existing.length})
                          </div>
                          <div className="space-y-1">
                            {existing.map((a) => (
                              <div
                                key={a.id || a.storage_path}
                                className="flex items-center justify-between gap-2 bg-white border rounded px-2 py-1 text-[11px]"
                                data-testid={`rm-bill-existing-attachment-row-${a.id || ""}`}
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <Paperclip className="w-3 h-3 text-zinc-400 shrink-0" />
                                  <span className="truncate text-zinc-800 font-medium" title={a.original_filename}>
                                    {a.original_filename || "(unnamed)"}
                                  </span>
                                  <span className="text-zinc-500 whitespace-nowrap font-mono text-[10px]">
                                    {(Number(a.size || 0) / 1024).toFixed(0)} KB
                                  </span>
                                  {a.zoho_synced ? (
                                    <span className="text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">
                                      Zoho ✓
                                    </span>
                                  ) : null}
                                </div>
                                <a
                                  href={`${API}/rm-inward/bills/${encodeURIComponent(editingBillId)}/attachments/${encodeURIComponent(a.id)}?download=true&auth=${encodeURIComponent(token)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline text-[11px] font-medium"
                                  data-testid={`rm-bill-existing-attachment-download-${a.id}`}
                                >
                                  Download
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    <div className="border-2 border-dashed rounded-lg p-3 text-center mt-1">
                      <input
                        type="file"
                        multiple
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf,.pdf"
                        onChange={(e) => {
                          const picked = Array.from(e.target.files || []);
                          setValidationResult(null);
                          if (picked.length === 0) return;
                          const accepted = [];
                          for (const f of picked) {
                            if (!ALLOWED_INVOICE_TYPES.includes(f.type)) {
                              toast.error(`"${f.name}" — unsupported type ${f.type}. Use JPEG/PNG/WebP/HEIC/PDF.`);
                              continue;
                            }
                            if (f.size > MAX_INVOICE_BYTES) {
                              toast.error(`"${f.name}" too large (${(f.size/1024/1024).toFixed(2)} MB). Max 5 MB.`);
                              continue;
                            }
                            // De-dup by name + size so the same file picked
                            // twice doesn't queue twice.
                            const dupe = invoiceFiles.some(
                              (x) => x.name === f.name && x.size === f.size
                            );
                            if (!dupe) accepted.push(f);
                          }
                          if (accepted.length > 0) {
                            setInvoiceFiles((prev) => [...prev, ...accepted]);
                          }
                          // Clear the file-input so re-picking the same file fires onChange.
                          e.target.value = "";
                        }}
                        className="hidden"
                        id="rm-bill-invoice-file"
                        data-testid="rm-bill-invoice-file-input"
                      />
                      <label htmlFor="rm-bill-invoice-file" className="cursor-pointer block">
                        {invoiceFiles.length > 0 ? (
                          <div className="text-amber-700 text-sm font-medium">
                            + Add another file
                          </div>
                        ) : (
                          <div className="text-zinc-500 text-sm">
                            <p>Click to attach vendor invoice file(s)</p>
                            <p className="text-xs">JPEG / PNG / WebP / HEIC / PDF • max 5 MB per file</p>
                          </div>
                        )}
                      </label>
                    </div>

                    {/* List of selected files with per-file remove */}
                    {invoiceFiles.length > 0 && (
                      <div className="mt-2 space-y-1.5" data-testid="rm-bill-invoice-files-list">
                        {invoiceFiles.map((f, i) => (
                          <div
                            key={`${f.name}-${f.size}-${i}`}
                            className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5 text-xs"
                            data-testid={`rm-bill-invoice-file-${i}`}
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-200 text-emerald-900 px-1.5 py-0.5 rounded">
                                {(f.type || "").split("/").pop() || "file"}
                              </span>
                              <span className="truncate text-emerald-900 font-medium" title={f.name}>
                                {f.name}
                              </span>
                              <span className="text-emerald-700/70 whitespace-nowrap">
                                {(f.size/1024).toFixed(0)} KB
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setInvoiceFiles((prev) => prev.filter((_, j) => j !== i))
                              }
                              className="text-red-600 hover:text-red-800 font-bold px-1"
                              aria-label={`Remove ${f.name}`}
                              data-testid={`rm-bill-invoice-file-remove-${i}`}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <div className="flex items-center gap-3 pt-1">
                          <button
                            type="button"
                            onClick={() => { setInvoiceFiles([]); setValidationResult(null); }}
                            className="text-xs text-red-600 hover:underline"
                            data-testid="rm-bill-invoice-clear-btn"
                          >
                            Remove all
                          </button>
                          <button
                            type="button"
                            onClick={handleValidateInvoice}
                            disabled={validating}
                            className="text-xs px-2 py-1 rounded bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-900 font-medium disabled:opacity-50"
                            data-testid="rm-bill-invoice-validate-btn"
                            title="Validate the first file against the form (AI)"
                          >
                            {validating ? "Validating…" : "Validate against invoice (AI)"}
                          </button>
                        </div>
                      </div>
                    )}

                    {validationResult && (
                      <div
                        className={`mt-3 border rounded-md p-3 text-xs ${
                          validationResult.mismatch_count === 0
                            ? "bg-green-50 border-green-300"
                            : "bg-amber-50 border-amber-300"
                        }`}
                        data-testid="rm-bill-invoice-validation-panel"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-semibold">
                            {validationResult.mismatch_count === 0
                              ? "✓ Invoice matches the form"
                              : `⚠ ${validationResult.mismatch_count} mismatch${validationResult.mismatch_count === 1 ? "" : "es"} found`}
                          </div>
                          <button
                            type="button"
                            onClick={() => setValidationResult(null)}
                            className="text-zinc-500 hover:text-zinc-800"
                            data-testid="rm-bill-invoice-validation-close"
                          >
                            ✕
                          </button>
                        </div>
                        <div className="text-[11px] text-zinc-600 mb-2">
                          Warn-only • Form lines: {validationResult.line_item_summary?.form_lines ?? 0} •
                          {" "}Invoice lines: {validationResult.line_item_summary?.invoice_lines ?? 0} •
                          {" "}Matched HSNs: {validationResult.line_item_summary?.matched_lines ?? 0}
                        </div>
                        {(validationResult.mismatches || []).length > 0 && (
                          <div className="max-h-40 overflow-y-auto rounded border border-amber-200 bg-white">
                            <table className="w-full text-[11px]">
                              <thead className="bg-amber-100/60 sticky top-0">
                                <tr>
                                  <th className="text-left px-2 py-1 font-semibold">Field</th>
                                  <th className="text-left px-2 py-1 font-semibold">Form</th>
                                  <th className="text-left px-2 py-1 font-semibold">Invoice</th>
                                  <th className="text-left px-2 py-1 font-semibold">Severity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {validationResult.mismatches.map((m, i) => (
                                  <tr key={i} className="border-t border-amber-100">
                                    <td className="px-2 py-1 font-mono">{m.field}</td>
                                    <td className="px-2 py-1 break-all">{String(m.form_value ?? "—")}</td>
                                    <td className="px-2 py-1 break-all">{String(m.invoice_value ?? "—")}</td>
                                    <td className={`px-2 py-1 font-semibold ${
                                      m.severity === "HIGH" ? "text-red-700"
                                      : m.severity === "MEDIUM" ? "text-amber-700"
                                      : "text-zinc-600"
                                    }`}>{m.severity}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <p className="mt-2 text-[11px] text-zinc-500">
                          This check is a warning only. You can still save the bill regardless of mismatches.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Calculations */}
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Sub Total</span>
                    <span className="font-mono font-medium">₹{totals.sub_total.toFixed(2)}</span>
                  </div>
                  
                  {/* Discount */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm w-20">Discount</span>
                    <Select 
                      value={totals.discount_type || undefined} 
                      onValueChange={(v) => setTotals({...totals, discount_type: v})}
                    >
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue placeholder="%" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">%</SelectItem>
                        <SelectItem value="amount">₹</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={totals.discount_value}
                      onChange={(e) => setTotals({...totals, discount_value: parseFloat(e.target.value) || 0})}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="no-spinner w-24 h-8 text-right font-mono"
                    />
                    <span className="font-mono text-sm ml-auto">-₹{totals.discount_amount.toFixed(2)}</span>
                  </div>

                  {/* TDS/TCS */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm w-20">TDS</span>
                    <Select
                      value={totals.tds_tcs || "NONE"}
                      onValueChange={(v) => setTotals({...totals, tds_tcs: v})}
                    >
                      <SelectTrigger className="w-44 h-8" data-testid="tds-select-trigger">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">None</SelectItem>
                        {tdsTaxes.map(t => (
                          <SelectItem key={t.id} value={t.id} data-testid={`tds-option-${t.id}`}>
                            {t.label || `${t.tax_name} ${t.rate}%`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setShowManageTds(true)}
                      title="Manage TDS taxes"
                      data-testid="manage-tds-btn"
                    >
                      <Settings2 className="w-4 h-4" />
                    </Button>
                    <span className="font-mono text-sm ml-auto">-₹{totals.tds_tcs_amount.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span>Tax Total</span>
                    <span className="font-mono">+₹{totals.tax_total.toFixed(2)}</span>
                  </div>

                  <Separator />

                  <div className="flex justify-between text-lg font-bold">
                    <span>Grand Total</span>
                    <span className="font-mono text-primary">₹{totals.grand_total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end mt-6 gap-3">
                <Button variant="outline" onClick={() => setShowDialog(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleSaveDraft}
                  disabled={submitting}
                  data-testid="save-as-draft-btn"
                  className="uppercase"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save as Draft
                </Button>
                <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-bill-btn" className="uppercase">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {billData.linked_no_invoice_entry_id ? "Saving Bill…" : "Creating Bill..."}
                    </>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4 mr-2" />
                      {/* When the bill is linked to a no-invoice entry, stock was already
                          credited at entry submission — the bill MUST NOT update inventory.
                          The backend short-circuits the stock-add via `stock_updated=true`;
                          the button label is reworded to match reality. */}
                      {billData.linked_no_invoice_entry_id
                        ? "Save Bill (stock already credited)"
                        : "Save Bill & Update Inventory"}
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Statistics Cards — Sep 2026: values come from the server's
          `stats` payload (branch-scoped, current calendar month,
          computed over the FULL collection) so they stay correct
          under pagination. */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-border border border-border mb-8" data-testid="rm-inward-stats">
        <div className="bg-white p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">
            Bills This Month
          </div>
          <div className="text-3xl font-black font-mono text-zinc-700" data-testid="stats-bills-this-month">
            {stats.bills_this_month}
          </div>
        </div>
        <div className="bg-white p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">
            Active RMs in Branch
          </div>
          <div className="text-3xl font-black font-mono text-primary">
            {Object.keys(branchInventory).length}
          </div>
        </div>
        <div className="bg-white p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">
            Total Qty (This Month)
          </div>
          <div className="text-3xl font-black font-mono text-zinc-700" data-testid="stats-qty-this-month">
            {(stats.qty_this_month || 0).toFixed(0)}
          </div>
        </div>
        <div className="bg-white p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">
            Total Value (This Month)
          </div>
          <div className="text-3xl font-black font-mono text-green-600" data-testid="stats-value-this-month">
            ₹{(stats.value_this_month || 0).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Sep 2026 — server-side filter bar (Status / Branch /
          Search-by-bill-number), the same pattern as the no-invoice
          tab. Every control re-queries the backend, which filters
          over the complete collection — nothing is filtered in
          browser state. */}
      <div className="border border-border bg-white rounded-sm p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="rm-inward-filter-bar">
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[170px]" data-testid="rm-inward-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              <SelectItem value="PLAYFIELD_DRAFT">Playfield Draft</SelectItem>
              <SelectItem value="DRAFT">Zoho Draft</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="PUSH_PENDING">Push Pending</SelectItem>
              <SelectItem value="PUSH_FAILED">Push Failed</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
              <SelectItem value="DELETED">Deleted</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase">Branch</Label>
          <Select value={viewBranch} onValueChange={setViewBranch}>
            <SelectTrigger className="w-[200px]" data-testid="rm-inward-branch-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All branches</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.branch_id || b.name} value={b.name}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search by bill number…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="h-9"
            data-testid="rm-inward-search"
          />
        </div>
      </div>

      {/* Sep 2026 — Timeline filter row, the same 4-mode pattern as
          the no-invoice tab (Last 90 days / By month / Date range /
          All time). Default = rolling last 90 days; the active window
          is always labeled; "All time" is one click. */}
      <div className="border border-border bg-white rounded-sm p-3 mb-6 flex flex-wrap items-center gap-3" data-testid="rm-inward-timeline-bar">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-zinc-400" />
          <Label className="text-xs uppercase">Timeline</Label>
          <Select
            value={timeline.mode}
            onValueChange={(v) =>
              setTimeline((tl) =>
                v === DATE_MODES.LAST_90 ? defaultTimeline()
                : { ...tl, mode: v }
              )
            }
          >
            <SelectTrigger className="w-[180px]" data-testid="inv-timeline-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DATE_MODES.LAST_90} data-testid="inv-timeline-last90">Last 90 days</SelectItem>
              <SelectItem value={DATE_MODES.MONTH} data-testid="inv-timeline-month-opt">By month</SelectItem>
              <SelectItem value={DATE_MODES.RANGE} data-testid="inv-timeline-range-opt">Date range</SelectItem>
              <SelectItem value={DATE_MODES.ALL} data-testid="inv-timeline-all-opt">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {timeline.mode === DATE_MODES.MONTH && (
          <Input
            type="month"
            value={timeline.month}
            onChange={(e) => setTimeline((tl) => ({ ...tl, month: e.target.value }))}
            className="h-9 w-[160px]"
            data-testid="inv-timeline-month"
          />
        )}

        {timeline.mode === DATE_MODES.RANGE && (
          <>
            <div className="flex items-center gap-1">
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={timeline.from}
                onChange={(e) => setTimeline((tl) => ({ ...tl, from: e.target.value }))}
                className="h-9 w-[150px]"
                data-testid="inv-timeline-from"
              />
            </div>
            <div className="flex items-center gap-1">
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={timeline.to}
                onChange={(e) => setTimeline((tl) => ({ ...tl, to: e.target.value }))}
                className="h-9 w-[150px]"
                data-testid="inv-timeline-to"
              />
            </div>
          </>
        )}

        {/* Active window label — always shown so history is never
            "silently hidden" behind the default 90-day scope. */}
        <Badge
          variant={timeline.mode === DATE_MODES.ALL ? "outline" : "secondary"}
          className="ml-1 font-mono text-xs"
          data-testid="inv-timeline-label"
        >
          {describeTimeline(timeline)}
        </Badge>

        {timeline.mode !== DATE_MODES.ALL && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setTimeline({ ...timeline, mode: DATE_MODES.ALL })}
            className="text-zinc-600"
            data-testid="inv-timeline-clear"
          >
            Show all time
          </Button>
        )}
      </div>

      {/* Inward Entries Table */}
      <div className="border border-border bg-white rounded-sm overflow-hidden">
        <div className="p-6 border-b border-border flex items-center gap-3">
          <Package className="w-5 h-5 text-primary" strokeWidth={1.5} />
          <h2 className="text-lg font-bold uppercase tracking-tight">Recent Inward Entries / Bills</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="inward-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Date</th>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Bill #</th>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Branch</th>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Vendor</th>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Items</th>
                <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Total Qty</th>
                <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Amount</th>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Status</th>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider w-16">View</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, idx) => (
                <tr key={entry.id || idx} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`inward-row-${entry.bill_id}`}>
                  <td className="p-4 align-middle font-mono text-zinc-700">
                    {new Date(entry.date).toLocaleDateString()}
                  </td>
                  <td className="p-4 align-middle font-mono text-sm text-primary">
                    {entry.bill_number || '-'}
                  </td>
                  <td className="p-4 align-middle text-sm font-mono text-zinc-700">
                    {entry.branch || '-'}
                  </td>
                  <td className="p-4 align-middle text-sm">
                    {entry.vendor_name || '-'}
                  </td>
                  <td className="p-4 align-middle text-sm">
                    {entry.item_count > 0 ? (
                      <span className="font-mono text-zinc-700">
                        {entry.item_count} {entry.item_count === 1 ? "item" : "items"}
                        {entry.item_count === 1 && entry.first_rm_id && (
                          <span className="ml-2 text-xs text-zinc-500">({entry.first_rm_id})</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-zinc-400 text-xs font-mono">-</span>
                    )}
                  </td>
                  <td className="p-4 align-middle font-mono text-primary font-bold text-right">
                    {entry.total_quantity > 0 ? `+${entry.total_quantity}` : "-"}
                  </td>
                  <td className="p-4 align-middle font-mono text-sm font-medium text-right">
                    ₹{Number(entry.total_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="p-4 align-middle">
                    {(() => {
                      const s = entry.status || "APPROVED";
                      const styles = {
                        DRAFT: "bg-amber-100 text-amber-800 border border-amber-200",
                        PLAYFIELD_DRAFT: "bg-zinc-200 text-zinc-800 border border-zinc-300",
                        APPROVED: "bg-green-100 text-green-800 border border-green-200",
                        REJECTED: "bg-red-100 text-red-800 border border-red-200",
                        PUSH_PENDING: "bg-amber-100 text-amber-800 border border-amber-200",
                        PUSH_FAILED: "bg-red-100 text-red-800 border border-red-200",
                        DELETED: "bg-zinc-100 text-zinc-500 line-through border border-zinc-300",
                      };
                      // UI-only label mapping. DB value is unchanged for
                      // every downstream consumer (webhook, reconciler,
                      // edit-bill gate, tests, etc.).
                      const labels = {
                        DRAFT: "ZOHO DRAFT",
                        PLAYFIELD_DRAFT: "PLAYFIELD DRAFT",
                        DELETED: "DELETED",
                      };
                      return (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase tracking-wider ${styles[s] || styles.APPROVED}`}
                            data-testid={`inward-status-${entry.bill_id}`}
                            title={entry.zoho_bill_status ? `Zoho: ${entry.zoho_bill_status}` : undefined}
                          >
                            {labels[s] || s}
                          </span>
                          {s === "PLAYFIELD_DRAFT" && entry.zoho_push_error && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono bg-amber-50 text-amber-900 border border-amber-300"
                              title={entry.zoho_push_error}
                              data-testid={`inward-zoho-error-${entry.bill_id}`}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              {(entry.zoho_push_error || "").slice(0, 40)}
                              {(entry.zoho_push_error || "").length > 40 ? "…" : ""}
                            </span>
                          )}
                          <ZohoPushStatusBadge
                            status={entry.zoho_push_status}
                            error={entry.zoho_push_error}
                            retryCount={entry.zoho_push_retry_count || 0}
                            testIdPrefix={`inward-${entry.bill_id}`}
                          />
                        </div>
                      );
                    })()}
                  </td>
                  <td className="p-4 align-middle">
                    <div className="flex items-center gap-1">
                      {entry.status === "PLAYFIELD_DRAFT" && (
                        <>
                          <PermButton
                            entity="RMInwardBill"
                            action="UPDATE"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleStartEdit(entry)}
                            title="Edit Playfield Draft"
                            data-testid={`draft-edit-${entry.bill_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </PermButton>
                          <PermButton
                            entity="RMInwardBill"
                            action="CREATE"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-green-700"
                            onClick={() => handleSubmitDraft(entry)}
                            title="Submit to Zoho"
                            data-testid={`draft-submit-${entry.bill_id}`}
                          >
                            <Send className="w-4 h-4" />
                          </PermButton>
                        </>
                      )}
                      {entry.bill_id ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setViewBillId(entry.bill_id)}
                          title="View bill detail"
                          data-testid={`view-bill-${entry.bill_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      ) : (
                        <span className="text-zinc-400 text-xs font-mono">-</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {entries.length === 0 && !loading && (
            <div className="p-12 text-center text-muted-foreground font-mono text-sm" data-testid="inward-empty">
              No inward entries match the current filters
            </div>
          )}
        </div>
        {/* Sep 2026 — pagination footer (same shared component as the
            no-invoice tab). Drives from server totals so every bill is
            reachable regardless of collection size. */}
        <div className="px-4 py-2 border-t border-zinc-200">
          <Pagination
            currentPage={page}
            totalPages={Math.max(1, Math.ceil(total / pageSize))}
            totalItems={total}
            pageSize={pageSize}
            pageSizeOptions={[10, 25, 50, 100]}
            onPageChange={setPage}
            onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
            loading={loading}
          />
        </div>
      </div>

      <ManageTdsDialog
        open={showManageTds}
        onOpenChange={setShowManageTds}
        onChanged={fetchTdsTaxes}
      />

      <RMInwardBillDetailDialog
        open={!!viewBillId}
        onOpenChange={(o) => !o && setViewBillId(null)}
        billId={viewBillId}
        onEdit={(bill) => {
          setViewBillId(null);
          handleStartEdit(bill);
        }}
        onDeleted={() => {
          // Refresh the list so the just-deleted row disappears (or
          // moves under the "Deleted" chip if that filter is active).
          fetchEntries();
        }}
      />
    </div>
  );
};

export default RMInward;
